"use client";

import { ArrowClockwiseIcon, HouseIcon, WarningIcon } from "@phosphor-icons/react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { useTranslation } from "@/i18n/client";

interface ErrorScreenProps {
  title?: string;
  message?: string;
  titleKey?: string;
  messageKey?: string;
  showGoBackButton?: boolean;
  showHomeButton?: boolean;
  children?: React.ReactNode;
  variant?: "404" | "error" | "critical";
}

export function ErrorScreen({
  title,
  message,
  titleKey,
  messageKey,
  showGoBackButton = true,
  showHomeButton = false,
  children,
  variant = "error",
}: ErrorScreenProps) {
  const router = useRouter();
  const { t } = useTranslation("global-error");
  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedMessage = messageKey ? t(messageKey) : message;

  const handleGoBack = () => {
    router.back();
  };

  const handleGoHome = () => {
    router.push("/");
  };

  const getIcon = () => {
    const iconClass = "h-20 w-20 mb-8";
    switch (variant) {
      case "404":
        return null;
      case "critical":
        return <WarningIcon className={`${iconClass} text-error-400`} />;
      default:
        return <ArrowClockwiseIcon className={`${iconClass} text-primary-600`} />;
    }
  };

  const getGradientClass = () => {
    switch (variant) {
      case "critical":
        return "bg-gradient-to-br from-error-100 via-neutral-50 to-background";
      default:
        return "bg-gradient-to-br from-primary-100 via-neutral-50 to-background";
    }
  };

  return (
    <div className={`min-h-screen ${getGradientClass()} flex items-center justify-center p-6`}>
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-secondary-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-1/3 w-24 h-24 bg-primary-300/30 rounded-full blur-2xl" />
      </div>

      <div className="relative z-10 w-full max-w-lg text-center">
        {/* Icon */}
        <div className="flex justify-center">{getIcon()}</div>

        {/* Title and Message in column layout */}
        <div className="mb-12 flex flex-col items-center">
          <Typography variant="heading 1" className="text-6xl font-bold mb-4 text-neutral-700">
            {resolvedTitle}
          </Typography>

          <Typography
            variant="body 1"
            className="text-lg text-neutral-600 leading-relaxed max-w-md mx-auto"
          >
            {resolvedMessage}
          </Typography>
        </div>

        {/* Action buttons */}
        <div className="space-y-3 md:flex md:flex-row gap-4 md:justify-center w-full flex-col">
          {children}

          {showHomeButton && (
            <Button onClick={handleGoHome} className="w-full" size="lg">
              <HouseIcon className="mr-2 h-5 w-5" />
              {t("actions.goHome")}
            </Button>
          )}

          {showGoBackButton && (
            <Button onClick={handleGoBack} variant="outline" className="w-full" size="lg">
              {t("actions.goBack")}
            </Button>
          )}
        </div>

        {/* Footer branding */}
        <div className="mt-16 pt-8 border-t border-neutral-200">
          <Typography variant="body 2" className="text-neutral-500">
            {t("footer.branding")}
          </Typography>
        </div>
      </div>
    </div>
  );
}
